/**
 * Centralized Database Path Configuration for Website
 *
 * All database paths are defined here to ensure consistency across the website.
 * Uses test-mode/v1 prefix when test mode is enabled via admin panel.
 * Uses v1 prefix in production.
 *
 * NOTE: This file provides path generators. For test-mode aware paths,
 * use the useTestModeDb hook or getPath from TestModeContext.
 */

export const TEST_PREFIX = 'test-mode/v1';
export const PROD_PREFIX = 'v1';

/**
 * Get path with optional test mode prefix
 */
export const getPath = (path: string, isTestMode: boolean): string => {
  const prefix = isTestMode ? TEST_PREFIX : PROD_PREFIX;
  return `${prefix}/${path}`;
};

/**
 * Database path definitions organized by category
 * These return relative paths that should be prefixed with getPath()
 */
export const DB_PATHS = {
  // ============================================
  // USERS
  // ============================================
  users: {
    /** User profile data - was: users/{uid} */
    profile: (uid: string) => `users/profiles/${uid}`,
    /** All user profiles */
    profiles: () => `users/profiles`,
    /** Username reservation/lookup - was: usernames/{username} */
    username: (username: string) => `users/usernames/${username}`,
    /** All usernames */
    usernames: () => `users/usernames`,
    /** Admin status - was: admins/{uid} */
    admin: (uid: string) => `users/admins/${uid}`,
    /** All admins */
    admins: () => `users/admins`,
    /** FCM tokens - was: users/{uid}/fcm */
    fcmToken: (uid: string) => `users/fcmTokens/${uid}`,
    /** Deletion requests - was: deletionRequests/{uid} */
    deletionRequest: (uid: string) => `users/deletionRequests/${uid}`,
  },

  // ============================================
  // WALLET (GT Tokens)
  // ============================================
  wallet: {
    /** User's GT balance - was: gtWallets/{uid} */
    balance: (uid: string) => `wallet/balances/${uid}`,
    /** All balances */
    balances: () => `wallet/balances`,
    /** User's transactions - was: gtTransactions/{uid} */
    transactions: (uid: string) => `wallet/transactions/${uid}`,
    /** Single transaction - was: gtTransactions/{uid}/{txId} */
    transaction: (uid: string, txId: string) => `wallet/transactions/${uid}/${txId}`,
    /** All GT packages - was: gtPackages */
    packages: () => `wallet/packages`,
    /** Single package - was: gtPackages/{packageId} */
    package: (packageId: string) => `wallet/packages/${packageId}`,
    /** Purchase record - was: gtPurchases/{purchaseId} */
    purchase: (purchaseId: string) => `wallet/purchases/${purchaseId}`,
    /** User's purchases - was: gtPurchasesByUser/{uid} */
    purchasesByUser: (uid: string) => `wallet/purchasesByUser/${uid}`,
    /** Saved cards - was: savedCards/{uid} */
    savedCards: (uid: string) => `wallet/savedCards/${uid}`,
  },

  // ============================================
  // COMMUNITY (Posts & Social)
  // ============================================
  community: {
    /** All posts - was: posts */
    posts: () => `community/posts`,
    /** Single post - was: posts/{postId} */
    post: (postId: string) => `community/posts/${postId}`,
    /** User's posts - was: userPosts/{uid} */
    postsByUser: (uid: string) => `community/postsByUser/${uid}`,
    /** User's single post index - was: userPosts/{uid}/{postId} */
    userPost: (uid: string, postId: string) => `community/postsByUser/${uid}/${postId}`,
    /** Posts by tag - was: postsByTag/{tag} */
    postsByTag: (tag: string) => `community/postsByTag/${tag}`,
    /** Post tag index - was: postsByTag/{tag}/{postId} */
    postByTag: (tag: string, postId: string) => `community/postsByTag/${tag}/${postId}`,
    /** Posts by team - was: postsByTeam/{team} */
    postsByTeam: (team: string) => `community/postsByTeam/${team}`,
    /** Post team index - was: postsByTeam/{team}/{postId} */
    postByTeam: (team: string, postId: string) => `community/postsByTeam/${team}/${postId}`,
    /** Posts by league - was: postsByLeague/{league} */
    postsByLeague: (league: string) => `community/postsByLeague/${league}`,
    /** Post league index - was: postsByLeague/{league}/{postId} */
    postByLeague: (league: string, postId: string) =>
      `community/postsByLeague/${league}/${postId}`,
    /** Posts by sport - was: postsBySport/{sport} */
    postsBySport: (sport: string) => `community/postsBySport/${sport}`,
    /** Post sport index - was: postsBySport/{sport}/{postId} */
    postBySport: (sport: string, postId: string) =>
      `community/postsBySport/${sport}/${postId}`,
    /** Feed index - was: feedIndex */
    feedIndex: () => `community/feedIndex`,
    /** Feed index entry - was: feedIndex/{postId} */
    feedEntry: (postId: string) => `community/feedIndex/${postId}`,
    /** Post comments - was: postComments/{postId} */
    comments: (postId: string) => `community/comments/${postId}`,
    /** Single comment - was: postComments/{postId}/{commentId} */
    comment: (postId: string, commentId: string) =>
      `community/comments/${postId}/${commentId}`,
    /** Post engagement metrics - was: postEngagement/{postId} */
    engagement: (postId: string) => `community/engagement/${postId}`,
  },

  // ============================================
  // INTERACTIONS (Likes, Saves, etc.)
  // ============================================
  interactions: {
    /** Post likes - was: postLikes/{postId} */
    postLikes: (postId: string) => `interactions/postLikes/${postId}`,
    /** User's post like - was: postLikes/{postId}/{uid} */
    postLike: (postId: string, uid: string) => `interactions/postLikes/${postId}/${uid}`,
    /** User's saved posts - was: postSaves/{uid} */
    postSaves: (uid: string) => `interactions/postSaves/${uid}`,
    /** Single saved post - was: postSaves/{uid}/{postId} */
    postSave: (uid: string, postId: string) => `interactions/postSaves/${uid}/${postId}`,
    /** News likes - was: newsLikes/{newsId} */
    newsLikes: (newsId: string) => `interactions/newsLikes/${newsId}`,
    /** User's news like - was: newsLikes/{newsId}/{uid} */
    newsLike: (newsId: string, uid: string) => `interactions/newsLikes/${newsId}/${uid}`,
    /** User's saved news - was: newsSaves/{uid} */
    newsSaves: (uid: string) => `interactions/newsSaves/${uid}`,
    /** Single saved news - was: newsSaves/{uid}/{newsId} */
    newsSave: (uid: string, newsId: string) => `interactions/newsSaves/${uid}/${newsId}`,
    /** Comment likes - was: commentLikes/{commentId} */
    commentLikes: (commentId: string) => `interactions/commentLikes/${commentId}`,
    /** User's comment like - was: commentLikes/{commentId}/{uid} */
    commentLike: (commentId: string, uid: string) =>
      `interactions/commentLikes/${commentId}/${uid}`,
  },

  // ============================================
  // SLIPS
  // ============================================
  slips: {
    /** All submissions - was: slipSubmissions */
    submissions: () => `slips/submissions`,
    /** Single submission - was: slipSubmissions/{submissionId} */
    submission: (submissionId: string) => `slips/submissions/${submissionId}`,
    /** Submissions by user - was: slipSubmissionsByUser/{uid} */
    byUser: (uid: string) => `slips/byUser/${uid}`,
    /** User's submission index - was: slipSubmissionsByUser/{uid}/{submissionId} */
    userSubmission: (uid: string, submissionId: string) =>
      `slips/byUser/${uid}/${submissionId}`,
    /** Submissions by status - was: slipSubmissionsByStatus/{status} */
    byStatus: (status: string) => `slips/byStatus/${status}`,
    /** Status submission index - was: slipSubmissionsByStatus/{status}/{submissionId} */
    statusSubmission: (status: string, submissionId: string) =>
      `slips/byStatus/${status}/${submissionId}`,
    /** Image hashes - was: slipImageHashes/{hash} */
    imageHash: (hash: string) => `slips/imageHashes/${hash}`,
    /** Virtual slips - was: virtualSlips/{uid} */
    virtual: (uid: string) => `slips/virtual/${uid}`,
    /** Single virtual slip - was: virtualSlips/{uid}/{slipId} */
    virtualSlip: (uid: string, slipId: string) => `slips/virtual/${uid}/${slipId}`,
    /** Active slips by user - was: activeSlipsByUser/{uid} */
    active: (uid: string) => `slips/active/${uid}`,
    /** Single active slip - was: activeSlipsByUser/{uid}/{slipId} */
    activeSlip: (uid: string, slipId: string) => `slips/active/${uid}/${slipId}`,
  },

  // ============================================
  // PICKS
  // ============================================
  picks: {
    /** User's picks - was: userPicks/{uid} */
    byUser: (uid: string) => `picks/byUser/${uid}`,
    /** Single user pick - was: userPicks/{uid}/{pickId} */
    userPick: (uid: string, pickId: string) => `picks/byUser/${uid}/${pickId}`,
    /** Picks by game - was: picksByGame/{gameId} */
    byGame: (gameId: string) => `picks/byGame/${gameId}`,
    /** Game pick index - was: picksByGame/{gameId}/{pickId} */
    gamePick: (gameId: string, pickId: string) => `picks/byGame/${gameId}/${pickId}`,
    /** User picks by game - was: userPicksByGame/{gameId} */
    userByGame: (gameId: string) => `picks/userByGame/${gameId}`,
    /** User game pick - was: userPicksByGame/{gameId}/{pickId} */
    userGamePick: (gameId: string, pickId: string) =>
      `picks/userByGame/${gameId}/${pickId}`,
    /** Picks by user index - was: picksByUser/{uid} */
    picksByUser: (uid: string) => `picks/picksByUser/${uid}`,
    /** User pick index entry - was: picksByUser/{uid}/{pickId} */
    pickByUser: (uid: string, pickId: string) => `picks/picksByUser/${uid}/${pickId}`,
  },

  // ============================================
  // EXPLORE FEED (Public picks & slips for discovery)
  // ============================================
  explore: {
    /** All public picks for explore feed */
    publicPicks: () => `explore/picks`,
    /** Single public pick */
    publicPick: (pickId: string) => `explore/picks/${pickId}`,
    /** All public slips for explore feed */
    publicSlips: () => `explore/slips`,
    /** Single public slip */
    publicSlip: (slipId: string) => `explore/slips/${slipId}`,
    /** Combined feed (picks + slips) sorted by hot/new */
    feed: () => `explore/feed`,
    /** Single feed item */
    feedItem: (itemId: string) => `explore/feed/${itemId}`,
    /** Engagement data for explore items */
    engagement: (itemId: string) => `explore/engagement/${itemId}`,
    /** Likes on explore item */
    likes: (itemId: string) => `explore/engagement/${itemId}/likes`,
    /** User like on explore item */
    like: (itemId: string, uid: string) => `explore/engagement/${itemId}/likes/${uid}`,
    /** Saves on explore item */
    saves: (itemId: string) => `explore/engagement/${itemId}/saves`,
    /** User save on explore item */
    save: (itemId: string, uid: string) => `explore/engagement/${itemId}/saves/${uid}`,
    /** Explore by user */
    byUser: (uid: string) => `explore/byUser/${uid}`,
    /** Explore by sport */
    bySport: (sport: string) => `explore/bySport/${sport}`,
    /** Explore by league */
    byLeague: (league: string) => `explore/byLeague/${league}`,
  },

  // ============================================
  // GAMES DATA (Upcoming games & schedule)
  // ============================================
  gamesData: {
    /** Upcoming games by league */
    byLeague: (leagueId: string) => `gamesData/byLeague/${leagueId}`,
    /** Single upcoming game */
    game: (leagueId: string, gameId: string) => `gamesData/byLeague/${leagueId}/${gameId}`,
    /** Games by date */
    byDate: (date: string) => `gamesData/byDate/${date}`,
    /** Single game by date */
    gameByDate: (date: string, gameId: string) => `gamesData/byDate/${date}/${gameId}`,
    /** History games by league */
    historyByLeague: (leagueId: string) => `gamesData/history/byLeague/${leagueId}`,
    /** Single history game */
    historyGame: (leagueId: string, gameId: string) =>
      `gamesData/history/byLeague/${leagueId}/${gameId}`,
    /** Last update timestamp */
    lastUpdate: () => `gamesData/lastUpdate`,
  },

  // ============================================
  // CHAT (Sweatrooms)
  // ============================================
  chat: {
    /** All rooms - was: sweatrooms */
    rooms: () => `chat/rooms`,
    /** Single room - was: sweatrooms/{roomId} */
    room: (roomId: string) => `chat/rooms/${roomId}`,
    /** User's rooms - was: sweatroomsByUser/{uid} */
    roomsByUser: (uid: string) => `chat/roomsByUser/${uid}`,
    /** User room index - was: sweatroomsByUser/{uid}/{roomId} */
    userRoom: (uid: string, roomId: string) => `chat/roomsByUser/${uid}/${roomId}`,
    /** Room by game - was: sweatroomsByGame/{gameId} */
    roomByGame: (gameId: string) => `chat/roomsByGame/${gameId}`,
    /** Rooms by status - was: sweatroomsByStatus/{status} */
    roomsByStatus: (status: string) => `chat/roomsByStatus/${status}`,
    /** Status room index - was: sweatroomsByStatus/{status}/{roomId} */
    statusRoom: (status: string, roomId: string) =>
      `chat/roomsByStatus/${status}/${roomId}`,
    /** Room participants - was: sweatroomParticipants/{roomId} */
    participants: (roomId: string) => `chat/participants/${roomId}`,
    /** Single participant - was: sweatroomParticipants/{roomId}/{uid} */
    participant: (roomId: string, uid: string) => `chat/participants/${roomId}/${uid}`,
    /** Room messages - was: sweatroomMessages/{roomId} */
    messages: (roomId: string) => `chat/messages/${roomId}`,
    /** Single message - was: sweatroomMessages/{roomId}/{messageId} */
    message: (roomId: string, messageId: string) =>
      `chat/messages/${roomId}/${messageId}`,
  },

  // ============================================
  // GAMES
  // ============================================
  games: {
    /** All game tracking - was: gameTracking */
    tracking: () => `games/tracking`,
    /** Single game tracking - was: gameTracking/{gameId} */
    game: (gameId: string) => `games/tracking/${gameId}`,
  },

  // ============================================
  // NEWS
  // ============================================
  news: {
    /** News cache - was: newsCache */
    cache: () => `news/cache`,
    /** Cached news entry - was: newsCache/{cacheKey} */
    cached: (cacheKey: string) => `news/cache/${cacheKey}`,
  },

  // ============================================
  // CONFIG
  // ============================================
  config: {
    /** App config - was: config */
    app: () => `config/app`,
    /** App config key - was: config/{key} */
    appKey: (key: string) => `config/app/${key}`,
    /** Founding member config - was: config/foundingMember */
    foundingMember: () => `config/app/foundingMember`,
    /** Signup offers - was: signupOffers */
    signupOffers: () => `config/signupOffers`,
    /** Single signup offer - was: signupOffers/{offerId} */
    signupOffer: (offerId: string) => `config/signupOffers/${offerId}`,
    /** Offer redemptions - was: signupOfferRedemptions */
    offerRedemptions: () => `config/offerRedemptions`,
    /** Single redemption - was: signupOfferRedemptions/{redemptionId} */
    offerRedemption: (redemptionId: string) => `config/offerRedemptions/${redemptionId}`,
    /** Profile tags - was: profileTags */
    profileTags: () => `config/profileTags`,
    /** Single profile tag - was: profileTags/{tagId} */
    profileTag: (tagId: string) => `config/profileTags/${tagId}`,
    /** OG Hub chat - was: ogHubChat */
    ogHubChat: () => `config/ogHubChat`,
  },

  // ============================================
  // STORE
  // ============================================
  store: {
    /** Store items - was: storeItems */
    items: () => `store/items`,
    /** Single store item - was: storeItems/{itemId} */
    item: (itemId: string) => `store/items/${itemId}`,
    /** Items by category - was: storeItemsByCategory/{category} */
    itemsByCategory: (category: string) => `store/itemsByCategory/${category}`,
    /** Store redemptions - was: storeRedemptions */
    redemptions: () => `store/redemptions`,
    /** Single redemption - was: storeRedemptions/{redemptionId} */
    redemption: (redemptionId: string) => `store/redemptions/${redemptionId}`,
    /** Redemptions by status - was: redemptionsByStatus/{status} */
    redemptionsByStatus: (status: string) => `store/redemptionsByStatus/${status}`,
  },

  // ============================================
  // REPORTS
  // ============================================
  reports: {
    /** All reports - was: reports */
    all: () => `reports`,
    /** Single report - was: reports/{reportId} */
    report: (reportId: string) => `reports/${reportId}`,
    /** Reports by status - was: reportsByStatus/{status} */
    byStatus: (status: string) => `reports/byStatus/${status}`,
  },
};

/**
 * Helper to create full path with test mode prefix
 */
export const createPath = (relativePath: string, isTestMode: boolean): string => {
  const prefix = isTestMode ? TEST_PREFIX : PROD_PREFIX;
  return `${prefix}/${relativePath}`;
};

export default DB_PATHS;
